import type { StoredDocument } from '@/modules/documents'
import { getStoredDocumentType } from '@/modules/file-reader/documentClassification'

export type AccountingDocumentMovement = 'incoming' | 'outgoing'

export function fiscalSituationDigits(value: unknown) {
  const normalized = String(value || '').replace(/\D/g, '')
  return normalized || null
}

export function isCfopCompatibleWithMovement(
  cfop: unknown,
  movement: AccountingDocumentMovement,
) {
  const normalized = String(cfop || '').replace(/\D/g, '')
  if (normalized.length !== 4) return false
  return movement === 'incoming'
    ? ['1', '2', '3'].includes(normalized[0])
    : ['5', '6', '7'].includes(normalized[0])
}

type AccountingMovementDocument = Pick<StoredDocument, 'metadata' | 'original_name' | 'document_kind'>

interface MovementFiscalSummary {
  summary_version?: unknown
  operation_type?: unknown
  issuer_tax_id?: unknown
  recipient_tax_id?: unknown
}

export function hasCurrentAccountingFiscalSummary(document: AccountingMovementDocument) {
  if (document.document_kind !== 'xml') return true
  const summary = document.metadata?.fiscal_summary as MovementFiscalSummary | undefined
  return Number(summary?.summary_version || 0) >= 3
}

export const normalizeTaxId = (value: unknown) => typeof value === 'string' ? value.replace(/\D/g, '') : ''

export function taxIdsRepresentSameCompany(left: unknown, right: unknown) {
  const normalizedLeft = normalizeTaxId(left)
  const normalizedRight = normalizeTaxId(right)
  if (!normalizedLeft || !normalizedRight) return false
  if (normalizedLeft === normalizedRight) return true

  return normalizedLeft.length === 14
    && normalizedRight.length === 14
    && normalizedLeft.slice(0, 8) === normalizedRight.slice(0, 8)
}

export function getAccountingDocumentMovement(
  document: AccountingMovementDocument,
  clientTaxId: string | null | undefined,
): AccountingDocumentMovement {
  const summary = document.metadata?.fiscal_summary as MovementFiscalSummary | undefined
  const normalizedClient = normalizeTaxId(clientTaxId)
  const normalizedIssuer = normalizeTaxId(summary?.issuer_tax_id)
  const normalizedRecipient = normalizeTaxId(summary?.recipient_tax_id)
  const isOwnIssue = taxIdsRepresentSameCompany(clientTaxId, summary?.issuer_tax_id)
  const isOwnDestination = taxIdsRepresentSameCompany(clientTaxId, summary?.recipient_tax_id)
  const isExactIssuer = Boolean(normalizedClient && normalizedClient === normalizedIssuer)
  const isExactRecipient = Boolean(normalizedClient && normalizedClient === normalizedRecipient)

  const documentType = getStoredDocumentType(document)
  const isOutgoingType = documentType === 'nfe'
    ? summary?.operation_type === '1'
    : documentType === 'cte' || documentType === 'nfse_nacional'

  if (isExactRecipient && !isExactIssuer) return 'incoming'
  if (isExactIssuer && isOutgoingType) return 'outgoing'
  if (isOwnDestination) return 'incoming'
  return isOwnIssue && isOutgoingType ? 'outgoing' : 'incoming'
}
